<template>
  <div :style="wrapperStyle" class="video-thumbnail-wrapper">
    <video
      ref="videoPlayer"
      :src="videoSrc"
      :poster="generatedPoster"
      style="width: 100%; height: 100%; object-fit: cover; display: block; background-color: #000;"
      muted
      playsinline
      preload="metadata"
    ></video>

    <div class="play-icon-overlay">
      <i class="fas fa-play"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoThumbnail',
  props: {
    videoSrc: {
      type: String,
      required: true,
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      generatedPoster: null,
      videoElementForThumbnail: null, // Referência para o vídeo temporário
    };
  },
  computed: {
    wrapperStyle() {
      return {
        width: this.width + 'px',
        height: this.height + 'px',
        overflow: 'hidden', // Garante que o vídeo não ultrapasse o wrapper
        display: 'inline-block', // Ou 'block' dependendo do fluxo de layout
        backgroundColor: '#000', // Fundo enquanto o poster não carrega
      };
    }
  },
  watch: {
    videoSrc: {
      immediate: true,
      handler(newSrc, oldSrc) {
        // Só regera se o src realmente mudou ou se é a carga inicial e não tem poster
        if (newSrc && (newSrc !== oldSrc || !this.generatedPoster)) {
          this.generateThumbnail(newSrc);
        } else if (!newSrc && oldSrc) { // videoSrc tornou-se nulo
          this.cleanupVideoElement();
          this.generatedPoster = null;
          if (this.$refs.videoPlayer) {
            this.$refs.videoPlayer.poster = ''; // Limpa o poster do vídeo visível
          }
        }
      },
    },
  },
  methods: {
    generateThumbnail(src) {
      this.cleanupVideoElement(); // Limpa qualquer instância anterior
      this.generatedPoster = null; // Reseta o poster

      const video = document.createElement('video');
      this.videoElementForThumbnail = video;

      video.crossOrigin = "anonymous";
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';

      const onSeeked = () => {
        // Verifica se o elemento ainda é o que estamos processando
        if (this.videoElementForThumbnail !== video) return;

        if (video.videoWidth > 0 && video.videoHeight > 0) {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = this.width; // Usar props para dimensões do poster
          canvas.height = this.height;

          const videoAspectRatio = video.videoWidth / video.videoHeight;
          const canvasAspectRatio = canvas.width / canvas.height;
          let sx = 0, sy = 0, sWidth = video.videoWidth, sHeight = video.videoHeight;

          // Lógica para 'object-fit: cover' no canvas
          if (videoAspectRatio > canvasAspectRatio) { // Vídeo mais largo que o canvas
            sWidth = video.videoHeight * canvasAspectRatio;
            sx = (video.videoWidth - sWidth) / 2;
          } else if (videoAspectRatio < canvasAspectRatio) { // Vídeo mais alto que o canvas
            sHeight = video.videoWidth / canvasAspectRatio;
            sy = (video.videoHeight - sHeight) / 2;
          }

          context.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
          this.generatedPoster = canvas.toDataURL('image/jpeg', 0.8);
        } else {
          console.warn("VideoThumbnail: Dimensões do vídeo não disponíveis após seek.", src.substring(0, 50) + "...");
        }
        this.cleanupVideoElement(); // Limpa após sucesso ou falha em obter dimensões
      };

      const onLoadedData = () => {
        if (this.videoElementForThumbnail !== video) return;

        if (video.readyState >= 1) { // HAVE_METADATA
          video.currentTime = 0.01; // Buscar um pouco depois do início; 0 pode ser problemático
        } else {
          console.warn("VideoThumbnail: Vídeo não pronto o suficiente em loadeddata.", src.substring(0, 50) + "...");
          this.cleanupVideoElement();
        }
      };

      const onError = (e) => {
        if (this.videoElementForThumbnail !== video) return;
        console.error('VideoThumbnail: Erro ao carregar vídeo para thumbnail:', e, src.substring(0, 50) + "...");
        this.generatedPoster = null; // Poderia definir um poster de erro aqui
        this.cleanupVideoElement();
      };

      // Usar { once: true } para que os listeners sejam removidos automaticamente
      video.addEventListener('loadeddata', onLoadedData, { once: true });
      video.addEventListener('seeked', onSeeked, { once: true });
      video.addEventListener('error', onError, { once: true });

      video.src = src;
      // video.load(); // Geralmente não é necessário, pois definir o src já inicia o carregamento
    },

    cleanupVideoElement() {
      if (this.videoElementForThumbnail) {
        const video = this.videoElementForThumbnail;
        // Remover listeners explicitamente é uma boa prática, embora {once: true} ajude
        // Para remover, precisaríamos das referências das funções, o que {once:true} evita
        video.removeAttribute('src'); // Libera o recurso
        video.load(); // Pede ao navegador para parar de carregar/processar
        this.videoElementForThumbnail = null;
      }
    }
  },
  beforeDestroy() {
    this.cleanupVideoElement();
  },
};
</script>

<style scoped>
.video-thumbnail-wrapper {
  /* Estilos adicionais para o wrapper, se necessário, podem ir aqui */
  /* Por exemplo, bordas, sombras, etc. */
  position: relative; /* Útil se você quiser adicionar sobreposições, como um ícone de play */
}

.play-icon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4); /* Fundo semi-transparente */
  color: white; /* Cor do ícone */
  font-size: 24px; /* Tamanho do ícone */
  pointer-events: none; /* Permite que o clique passe para o elemento abaixo (o link) */
  opacity: 1; /* Ícone sempre visível */
  transition: opacity 0.2s ease-in-out;
}

.video-thumbnail-wrapper:hover .play-icon-overlay {
  opacity: 1; /* Manter visível ou aumentar opacidade no hover se desejar */
}
</style>
